import { useState, useEffect, useRef, useLayoutEffect, useMemo } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useSearch, useNavigate } from "@tanstack/react-router";
import { useAuth } from "@/hooks/useAuth";
import { regionApi } from "@/lib/api/admin-api";
import type { Region } from "@/types/region";
import { useLingui } from "@lingui/react/macro";
import {
  Search,
  CheckCircle,
  ArrowUpDown,
  SlidersHorizontal,
  List,
  LayoutGrid,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Pagination } from "@/components/ui/pagination";
import { ListSectionHeader } from "@/components/common/ListSectionHeader";
import { useDebounce } from "use-debounce";
import { RegionCard } from "./RegionCard";
import { CreateRegionDialog } from "./CreateRegionDialog";

export function RegionListing() {
  const { t } = useLingui();
  const { isAuthenticated } = useAuth();
  const searchParams = useSearch({ from: "/(protected)/_layout/_org/regions" });
  const navigate = useNavigate();

  const [localSearch, setLocalSearch] = useState(searchParams.search ?? "");
  const [debouncedSearch] = useDebounce(localSearch, 400);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  // Stable ref so the debounce effect doesn't re-subscribe on every render.
  const navigateRef = useRef(navigate);
  useLayoutEffect(() => {
    navigateRef.current = navigate;
  });

  useEffect(() => {
    if (debouncedSearch !== (searchParams.search ?? "")) {
      void navigateRef.current({
        from: "/regions",
        search: (prev) => ({
          ...prev,
          search: debouncedSearch || undefined,
          page: 1,
        }),
        replace: true,
      });
    }
  }, [debouncedSearch, searchParams.search]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["regions", "list", searchParams],
    queryFn: () =>
      regionApi.paginate({
        page: searchParams.page,
        limit: searchParams.pageSize,
        search: searchParams.search || undefined,
        include: ["locations"],
      }),
    enabled: isAuthenticated,
    placeholderData: keepPreviousData,
  });

  const regions: Region[] = useMemo(() => data?.data ?? [], [data]);
  const meta = data?.meta;

  const groupedRegions = useMemo(() => {
    const groups = new Map<number | null, Region[]>();
    for (const region of regions) {
      const key = region.companyId ?? null;
      const list = groups.get(key) ?? [];
      list.push(region);
      groups.set(key, list);
    }
    // Sort: assigned companies first (in company name order), unassigned last
    const sorted: Array<{
      companyId: number | null;
      name: string;
      regions: Region[];
    }> = [];
    for (const [companyId, list] of groups) {
      // Each region carries its company relation — use the first one for the group label
      const companyName =
        companyId !== null ? (list[0]?.company?.name ?? "") : "";
      sorted.push({ companyId, name: companyName, regions: list });
    }
    sorted.sort((a, b) => {
      if (a.companyId === null) return 1;
      if (b.companyId === null) return -1;
      return a.name.localeCompare(b.name);
    });
    return sorted;
  }, [regions]);

  const goToPage = (page: number) =>
    void navigate({
      from: "/regions",
      search: (prev) => ({ ...prev, page }),
      replace: true,
    });

  const toggle = (id: number) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  return (
    <div className="flex flex-col gap-3">
      {/* Section header */}
      <ListSectionHeader
        title={t`Regions`}
        count={!isLoading ? meta?.total : undefined}
      >
        <Button
          variant="outline"
          className="h-10 gap-2 rounded-[10px] border-(--shell-border) text-sm font-medium text-(--color-text-primary)"
        >
          <CheckCircle className="size-4" />
          {t`Select`}
        </Button>
        <CreateRegionDialog />
      </ListSectionHeader>

      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="relative w-75">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-(--color-text-secondary)" />
          <Input
            placeholder={t`Search...`}
            className="pl-9 h-11 text-base border-(--shell-border) shadow-xs rounded-lg"
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            disabled
            className="h-11 gap-2 rounded-[10px] border-(--shell-border) opacity-50 text-sm font-medium text-(--color-text-primary)"
          >
            <ArrowUpDown className="size-4" />
            {t`Columns`}
          </Button>
          <Button
            variant="outline"
            className="h-11 gap-2 rounded-[10px] border-(--shell-border) text-sm font-medium text-(--color-text-primary)"
          >
            <ArrowUpDown className="size-4" />
            {t`Sort`}
          </Button>
          <Button
            variant="outline"
            className="h-11 gap-2 rounded-[10px] border-(--shell-border) text-sm font-medium text-(--color-text-primary)"
          >
            <SlidersHorizontal className="size-4" />
            {t`Filter`}
          </Button>
          <div className="flex items-center bg-(--color-surface-muted) rounded-lg p-1 h-11">
            <button
              type="button"
              className="size-9 flex items-center justify-center rounded-md bg-(--color-surface-content)"
            >
              <List className="size-4 text-(--color-text-primary)" />
            </button>
            <button
              type="button"
              className="size-9 flex items-center justify-center rounded-md"
            >
              <LayoutGrid className="size-4 text-(--color-text-secondary)" />
            </button>
          </div>
        </div>
      </div>

      {/* Cards */}
      {isLoading ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-21.5 w-full rounded-[10px]" />
          ))}
        </div>
      ) : (
        <div className="relative flex flex-col gap-6">
          {isFetching && !isLoading && (
            <div className="absolute inset-0 z-10 bg-white/50 backdrop-blur-[1px] rounded-[10px]" />
          )}
          {groupedRegions.length > 0 ? (
            groupedRegions.map(({ companyId, name, regions: groupRegions }) => (
              <div
                key={companyId ?? "unassigned"}
                className="flex flex-col gap-3"
              >
                <div className="flex items-center gap-2 px-1">
                  <span className="text-sm font-semibold text-(--color-text-primary)">
                    {companyId !== null ? name : t`Unassigned`}
                  </span>
                  <span className="text-xs text-(--color-text-secondary) bg-(--color-surface-muted) px-2 py-0.5 rounded-full">
                    {groupRegions.length}
                  </span>
                </div>
                {groupRegions.map((region) => (
                  <RegionCard
                    key={region.id}
                    region={region}
                    expanded={expandedIds.has(region.id)}
                    onToggle={() => toggle(region.id)}
                  />
                ))}
              </div>
            ))
          ) : (
            <p className="py-16 text-center text-sm text-(--color-text-secondary)">
              {t`No regions found`}
            </p>
          )}
        </div>
      )}

      {/* Pagination footer */}
      {!isLoading && meta && (
        <Pagination
          page={meta.page}
          totalPages={meta.totalPages}
          total={meta.total}
          limit={meta.limit}
          hasNext={meta.hasNext}
          hasPrev={meta.hasPrev}
          onPageChange={goToPage}
          onLimitChange={(limit) =>
            void navigate({
              from: "/regions",
              search: (prev) => ({ ...prev, pageSize: limit, page: 1 }),
              replace: true,
            })
          }
          className="pt-2"
        />
      )}
    </div>
  );
}
